import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './../assets/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashBookingData from './DashBookingData';
import url from './url';
import BusinessAccountLedger from './BusinessAccountLedger';

const formatMoney = (value) => {
    const amount = Number(value);
    return Number.isNaN(amount)
        ? '$0.00'
        : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
};

const statusClass = (status = '') => status.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
const isBookedStatus = (status = '') => ['booked', 'confirmed'].includes(status.toString().trim().toLowerCase());

function Dashboard() {
    const navigate = useNavigate();
    const { isAdmin, logout } = useAuth();
    const [dashboardSummary, setDashboardSummary] = useState({
        totalCustomers: 0,
        totalBookings: 0,
        totalRevenue: 0,
    });
    const [bookingsData, setBookingsData] = useState({
        bookings: [],
        pagination: { currentPage: 1, totalPages: 0, totalBookings: 0, limit: 10 },
    });
    const [markup, setMarkup] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSavingMarkup, setIsSavingMarkup] = useState(false);
    const [updatingBookingId, setUpdatingBookingId] = useState(null);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);
    const [reconcilingInsuranceId, setReconcilingInsuranceId] = useState(null);
    const [insuranceReconcileMessage, setInsuranceReconcileMessage] = useState('');
    const insuranceReconcileInProgress = useRef(false);
    const [activeSection, setActiveSection] = useState('bookings');
    const [usersData, setUsersData] = useState({
        users: [],
        pagination: { currentPage: 1, totalPages: 0, totalUsers: 0, limit: 10 },
    });
    const [userSearch, setUserSearch] = useState('');
    const [userStatus, setUserStatus] = useState('');
    const [selectedUserDetails, setSelectedUserDetails] = useState(null);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const [contentPages, setContentPages] = useState([]);
    const [selectedContentSlug, setSelectedContentSlug] = useState('faq');
    const [isSavingContent, setIsSavingContent] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordStep, setPasswordStep] = useState('request');
    const [currentPassword, setCurrentPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
    const searchTimeoutRef = useRef(null);
    const userSearchTimeoutRef = useRef(null);

    // Business accounts state
    const [businessData, setBusinessData] = useState({
        accounts: [],
        pagination: { currentPage: 1, totalPages: 0, total: 0, limit: 20 },
    });
    const [businessSearch, setBusinessSearch] = useState('');
    const [businessSearchTimeout, setBusinessSearchTimeout] = useState(null);
    const [createBusinessOpen, setCreateBusinessOpen] = useState(false);
    const [newBusiness, setNewBusiness] = useState({ name: '', email: '', password: '' });
    const [isCreatingBusiness, setIsCreatingBusiness] = useState(false);
    const [createBusinessError, setCreateBusinessError] = useState('');
    const [selectedBusinessAccount, setSelectedBusinessAccount] = useState(null); // detail modal
    const [isLoadingBusinessDetail, setIsLoadingBusinessDetail] = useState(false);

    const adminHeaders = (includeJson = false) => ({
        ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    });

    useEffect(() => {
        if (!isAdmin) {
            navigate('/AdminLogin');
        }
    }, [isAdmin, navigate]);

    const fetchBookingsOnly = async (page = 1, search = '', status = '', payment = '') => {
        const params = new URLSearchParams({ page: page.toString() });
        if (search) params.append('search', search);
        if (status) params.append('status', status);
        if (payment) params.append('payment', payment);

        const bookingsResponse = await fetch(`${url}/api/dash-stats/bookings?${params.toString()}`, { headers: adminHeaders() });
        if (!bookingsResponse.ok) throw new Error('Failed to fetch bookings');
        setBookingsData(await bookingsResponse.json());
    };

    const fetchUsers = async (page = 1, search = userSearch, status = userStatus) => {
        const params = new URLSearchParams({ page: page.toString() });
        if (search) params.append('search', search);
        if (status) params.append('status', status);
        const response = await fetch(`${url}/api/admin-users?${params.toString()}`, { headers: adminHeaders() });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Failed to fetch users');
        setUsersData(result);
    };

    const fetchBusinessAccounts = async (page = 1, search = businessSearch) => {
        const params = new URLSearchParams({ page: page.toString() });
        if (search) params.append('search', search);
        const response = await fetch(`${url}/api/admin/business-accounts?${params.toString()}`, { headers: adminHeaders() });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Failed to fetch business accounts');
        setBusinessData(result);
    };

    const fetchContentPages = async () => {
        const response = await fetch(`${url}/api/site-content/admin`, { headers: adminHeaders() });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Failed to fetch site content');
        setContentPages(result.pages || []);
    };

    const fetchDashboardData = async (page = 1, search = searchQuery, status = statusFilter, payment = paymentFilter) => {
        try {
            setError('');
            const [summaryResponse, markupResponse] = await Promise.all([
                fetch(`${url}/api/dash-stats/dashboard-summary`, { headers: adminHeaders() }),
                fetch(`${url}/api/dash-stats/markup`, { headers: adminHeaders() }),
            ]);
            if (!summaryResponse.ok || !markupResponse.ok) throw new Error('Unable to load dashboard summary.');
            const summaryData = await summaryResponse.json();
            const markupData = await markupResponse.json();
            setDashboardSummary(summaryData);
            setMarkup(markupData.markup);
            await Promise.all([
                fetchBookingsOnly(page, search, status, payment),
                fetchUsers(usersData.pagination.currentPage || 1),
                fetchContentPages(),
                fetchBusinessAccounts(1, ''),
            ]);
        } catch (fetchError) {
            setError(fetchError.message || 'Unable to load the admin dashboard.');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (isAdmin) fetchDashboardData();
        // fetchDashboardData intentionally uses the current filter values on first load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        setFeedback('');
        fetchDashboardData(bookingsData.pagination.currentPage);
    };

    const handleSearch = (event) => {
        const value = event.target.value;
        setSearchQuery(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            fetchBookingsOnly(1, value, statusFilter, paymentFilter).catch((fetchError) => setError(fetchError.message));
        }, 300);
    };

    const handleFilterChange = (setter, value, otherFilter) => {
        setter(value);
        const nextStatus = setter === setStatusFilter ? value : statusFilter;
        const nextPayment = setter === setPaymentFilter ? value : otherFilter;
        fetchBookingsOnly(1, searchQuery, nextStatus, nextPayment).catch((fetchError) => setError(fetchError.message));
    };

    const clearFilters = () => {
        setSearchQuery('');
        setStatusFilter('');
        setPaymentFilter('');
        fetchBookingsOnly(1, '', '', '').catch((fetchError) => setError(fetchError.message));
    };

    const handleUserSearch = (event) => {
        const value = event.target.value;
        setUserSearch(value);
        if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
        userSearchTimeoutRef.current = setTimeout(() => {
            fetchUsers(1, value, userStatus).catch((fetchError) => setError(fetchError.message));
        }, 300);
    };

    const handleUserStatusChange = (value) => {
        setUserStatus(value);
        fetchUsers(1, userSearch, value).catch((fetchError) => setError(fetchError.message));
    };

    const clearUserFilters = () => {
        setUserSearch('');
        setUserStatus('');
        fetchUsers(1, '', '').catch((fetchError) => setError(fetchError.message));
    };


    const handleBusinessSearch = (event) => {
        const value = event.target.value;
        setBusinessSearch(value);
        if (businessSearchTimeout) clearTimeout(businessSearchTimeout);
        setBusinessSearchTimeout(setTimeout(() => {
            fetchBusinessAccounts(1, value).catch((err) => setError(err.message));
        }, 300));
    };

    const handleCreateBusiness = async (event) => {
        event.preventDefault();
        setCreateBusinessError('');
        setIsCreatingBusiness(true);
        try {
            const response = await fetch(`${url}/api/admin/business-accounts`, {
                method: 'POST',
                headers: adminHeaders(true),
                body: JSON.stringify({
                    name: newBusiness.name,
                    email: newBusiness.email,
                    password: newBusiness.password,
                }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Failed to create account');
            setCreateBusinessOpen(false);
            setNewBusiness({ name: '', email: '', password: '' });
            setFeedback(`Business account for ${result.account.Name} created successfully.`);
            await fetchBusinessAccounts(1, businessSearch);
        } catch (err) {
            setCreateBusinessError(err.message);
        } finally {
            setIsCreatingBusiness(false);
        }
    };

    const fetchBusinessAccountDetail = async (accountId) => {
        setIsLoadingBusinessDetail(true);
        setSelectedBusinessAccount(null);
        setError('');
        try {
            const response = await fetch(`${url}/api/admin/business-accounts/${accountId}`, { headers: adminHeaders() });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Failed to load account details');
            setSelectedBusinessAccount(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoadingBusinessDetail(false);
        }
    };

    const handleBlockBusinessAccount = async (account) => {
        const nextBlocked = !account.Is_Blocked;
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/admin/business-accounts/${account.Cust_ID}/block`, {
                method: 'PATCH',
                headers: adminHeaders(true),
                body: JSON.stringify({ blocked: nextBlocked }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Failed to update account');
            setFeedback(`${account.Name} has been ${nextBlocked ? 'blocked' : 'unblocked'}.`);
            await fetchBusinessAccounts(businessData.pagination.currentPage, businessSearch);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteBusinessAccount = async (account) => {
        if (!window.confirm(`Deactivate ${account.Name}'s business account?\n\nThe account will lose access immediately but their booking history will be retained.`)) return;
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/admin/business-accounts/${account.Cust_ID}`, {
                method: 'DELETE',
                headers: adminHeaders(),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Failed to deactivate account');
            setFeedback(`${account.Name}'s business account was deactivated.`);
            await fetchBusinessAccounts(1, businessSearch);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleMarkupChange = async (event) => {
        event.preventDefault();
        setIsSavingMarkup(true);
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/dash-stats/markup`, {
                method: 'PUT',
                headers: adminHeaders(true),
                body: JSON.stringify({ newMarkup: markup }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Failed to update markup');
            setFeedback('Booking markup updated successfully.');
            await fetchDashboardData(bookingsData.pagination.currentPage);
        } catch (updateError) {
            setError(updateError.message);
        } finally {
            setIsSavingMarkup(false);
        }
    };

    const handleStatusChange = async (bookingId, currentStatus) => {
        const newStatus = isBookedStatus(currentStatus) ? 'Pending' : 'Booked';
        setUpdatingBookingId(bookingId);
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/dash-stats/bookings/${bookingId}/status`, {
                method: 'PUT',
                headers: adminHeaders(true),
                body: JSON.stringify({ newStatus }),
            });
            if (!response.ok) throw new Error('Failed to update booking status');
            setFeedback(`Booking #${bookingId} marked ${newStatus.toLowerCase()}.`);
            await fetchDashboardData(bookingsData.pagination.currentPage);
        } catch (updateError) {
            setError(updateError.message);
        } finally {
            setUpdatingBookingId(null);
        }
    };

    const fetchBookingDetails = async (bookingId) => {
        try {
            setError('');
            setInsuranceReconcileMessage('');
            const response = await fetch(`${url}/api/dash-stats/bookings/${bookingId}/details`, { headers: adminHeaders() });
            if (!response.ok) throw new Error('Failed to fetch booking details');
            setSelectedBookingDetails(await response.json());
        } catch (detailError) {
            setError(detailError.message);
        }
    };

    const reconcileInsurance = async (booking) => {
        const bookingId = booking?.Booking_ID || booking?.bookingId;
        if (!bookingId || insuranceReconcileInProgress.current) return;

        insuranceReconcileInProgress.current = true;
        setReconcilingInsuranceId(bookingId);
        setInsuranceReconcileMessage('');
        try {
            const response = await fetch(`${url}/api/insurance/reconcile/${bookingId}`, {
                method: 'POST',
                headers: adminHeaders(),
            });
            const result = await response.json().catch(() => ({})) || {};
            if (!response.ok) throw new Error(result.error || result.message || 'Unable to check insurance status.');

            const previousInsurance = booking.Insurance || booking.insurance || {};
            const returnedInsurance = result.insurance && typeof result.insurance === 'object' ? result.insurance : {};
            const updatedInsurance = {
                ...previousInsurance,
                ...returnedInsurance,
                ...(result.insuranceStatus != null ? { status: result.insuranceStatus } : {}),
                ...(result.insuranceMessage != null ? { message: result.insuranceMessage } : {}),
            };
            const latestStatus = updatedInsurance.status || updatedInsurance.Status || '';
            const normalizedStatus = String(latestStatus).toLowerCase();

            setSelectedBookingDetails((current) => {
                if (!current || (current.Booking_ID || current.bookingId) !== bookingId) return current;
                return {
                    ...current,
                    Insurance: updatedInsurance,
                    ...(current.insurance ? { insurance: updatedInsurance } : {}),
                    ...(result.insuranceStatus != null ? { insuranceStatus: result.insuranceStatus } : {}),
                    ...(result.insuranceMessage != null ? { insuranceMessage: result.insuranceMessage } : {}),
                };
            });

            if (normalizedStatus.includes('pending') || normalizedStatus.includes('fail')) {
                setInsuranceReconcileMessage(`Insurance remains ${latestStatus || 'unconfirmed'}; it is not confirmed. Please contact support for assistance.`);
            } else {
                setInsuranceReconcileMessage(result.insuranceMessage || 'Latest insurance status retrieved from the provider and shown above.');
            }
        } catch (reconcileError) {
            setInsuranceReconcileMessage(`Unable to check insurance status: ${reconcileError.message} Insurance remains unconfirmed; please contact support for assistance.`);
        } finally {
            insuranceReconcileInProgress.current = false;
            setReconcilingInsuranceId(null);
        }
    };

    const fetchUserDetails = async (userId) => {
        try {
            setError('');
            const response = await fetch(`${url}/api/admin-users/${userId}`, { headers: adminHeaders() });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Failed to fetch user profile');
            setSelectedUserDetails(result);
        } catch (detailError) {
            setError(detailError.message);
        }
    };

    const handleBlockUser = async (user) => {
        const nextBlocked = !user.Is_Blocked;
        setUpdatingUserId(user.Cust_ID);
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/admin-users/${user.Cust_ID}/block`, {
                method: 'PATCH',
                headers: adminHeaders(true),
                body: JSON.stringify({ blocked: nextBlocked }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Unable to update this user');
            setFeedback(`${user.Name} has been ${nextBlocked ? 'blocked' : 'unblocked'}.`);
            await Promise.all([fetchUsers(usersData.pagination.currentPage), fetchDashboardData(bookingsData.pagination.currentPage)]);
            if (selectedUserDetails?.user?.Cust_ID === user.Cust_ID) {
                await fetchUserDetails(user.Cust_ID);
            }
        } catch (updateError) {
            setError(updateError.message);
        } finally {
            setUpdatingUserId(null);
        }
    };

    const handleDeleteUser = async (user) => {
        const confirmed = window.confirm(
            `Deactivate ${user.Name}'s account?\n\nThis is a soft delete. The customer will lose access immediately, but their profile, drafts, and historical bookings will remain stored.`
        );
        if (!confirmed) return;

        setUpdatingUserId(user.Cust_ID);
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/admin-users/${user.Cust_ID}`, {
                method: 'DELETE',
                headers: adminHeaders(),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Unable to delete this user');
            setSelectedUserDetails(null);
            setFeedback(`${user.Name}'s account was deactivated. Their profile and ${result.preservedBookings || 0} historical booking(s) were retained.`);
            await fetchDashboardData(1);
        } catch (deleteError) {
            setError(deleteError.message);
        } finally {
            setUpdatingUserId(null);
        }
    };

    const updateContentDraft = (field, value) => {
        setContentPages((pages) => pages.map((page) => (
            page.slug === selectedContentSlug ? { ...page, [field]: value } : page
        )));
    };

    const handleSaveContent = async () => {
        const page = contentPages.find((item) => item.slug === selectedContentSlug);
        if (!page) return;

        setIsSavingContent(true);
        setError('');
        setFeedback('');
        try {
            const response = await fetch(`${url}/api/site-content/admin/${selectedContentSlug}`, {
                method: 'PUT',
                headers: adminHeaders(true),
                body: JSON.stringify({ title: page.title, body: page.body }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Unable to save page content');
            setContentPages((pages) => pages.map((item) => item.slug === result.page.slug ? result.page : item));
            setFeedback(`${result.page.title} was updated successfully.`);
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setIsSavingContent(false);
        }
    };

    const closePasswordModal = () => {
        if (isPasswordSubmitting) return;
        setPasswordModalOpen(false);
        setPasswordStep('request');
        setCurrentPassword('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setPasswordMessage('');
    };

    const handleRequestPasswordOtp = async (event) => {
        event.preventDefault();
        setPasswordError('');
        setPasswordMessage('');
        setIsPasswordSubmitting(true);
        try {
            const response = await fetch(`${url}/api/admin-password/request-otp`, {
                method: 'POST',
                headers: adminHeaders(true),
                body: JSON.stringify({ currentPassword }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Unable to send the verification code');
            setPasswordStep('verify');
            setPasswordMessage(result.message);
        } catch (requestError) {
            setPasswordError(requestError.message);
        } finally {
            setIsPasswordSubmitting(false);
        }
    };

    const handleConfirmPasswordChange = async (event) => {
        event.preventDefault();
        setPasswordError('');
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }
        if (newPassword.length < 12) {
            setPasswordError('New password must be at least 12 characters');
            return;
        }

        setIsPasswordSubmitting(true);
        try {
            const response = await fetch(`${url}/api/admin-password/confirm`, {
                method: 'POST',
                headers: adminHeaders(true),
                body: JSON.stringify({ otp, newPassword }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.error || 'Unable to change the password');
            closePasswordModal();
            logout();
            navigate('/AdminLogin', { replace: true, state: { message: result.message } });
        } catch (confirmError) {
            setPasswordError(confirmError.message);
        } finally {
            setIsPasswordSubmitting(false);
        }
    };

    if (!isAdmin) return null;

    if (isLoading) {
        return (
            <main className="admin-page">
                <div className="container admin-loading" role="status">
                    <span className="loading-line loading-line-wide"></span>
                    <span className="loading-line"></span>
                    <div className="loading-card-grid"><span></span><span></span><span></span></div>
                    <span className="visually-hidden">Loading dashboard…</span>
                </div>
            </main>
        );
    }

    return (
        <main className="admin-page">
            <div className="container">
                <header className="admin-header">
                    <div>
                        <span className="section-kicker">COURIERLINK OPERATIONS</span>
                        <h1>Good to see you, <em>admin.</em></h1>
                        <p>Monitor the platform, manage bookings, and keep your shipping margin up to date.</p>
                    </div>
                    <div className="admin-header-actions">
                        <span className="admin-session"><i></i>Admin session active</span>
                        <button type="button" className="admin-header-button" onClick={handleRefresh} disabled={isRefreshing}>
                            {isRefreshing ? 'Refreshing…' : 'Refresh data ↻'}
                        </button>
                        <button type="button" className="admin-header-button" onClick={() => setPasswordModalOpen(true)}>
                            Change password
                        </button>
                        <button type="button" className="admin-header-button admin-signout" onClick={() => { logout(); navigate('/AdminLogin'); }}>
                            Sign out
                        </button>
                    </div>
                </header>

                {error && <div className="admin-feedback admin-feedback-error" role="alert">{error}</div>}
                {feedback && <div className="admin-feedback admin-feedback-success" role="status">{feedback}</div>}

                <section className="admin-overview-grid" aria-label="Dashboard overview">
                    <article className="admin-stat-card admin-stat-featured">
                        <span className="section-kicker">TOTAL REVENUE</span>
                        <strong>{formatMoney(dashboardSummary.totalRevenue)}</strong>
                        <span>recorded across all bookings</span>
                    </article>
                    <article className="admin-stat-card">
                        <span className="section-kicker">CUSTOMERS</span>
                        <strong>{dashboardSummary.totalCustomers}</strong>
                        <span>registered accounts</span>
                    </article>
                    <article className="admin-stat-card">
                        <span className="section-kicker">BOOKINGS</span>
                        <strong>{dashboardSummary.totalBookings}</strong>
                        <span>shipments in the system</span>
                    </article>
                    <form className="admin-markup-card" onSubmit={handleMarkupChange}>
                        <div>
                            <span className="section-kicker">CUSTOMER MARKUP</span>
                            <strong>{markup}%</strong>
                            <span>applied to regular customers</span>
                        </div>
                        <div className="admin-markup-controls">
                            <label htmlFor="admin-markup">Update percentage</label>
                            <div>
                                <input
                                    id="admin-markup"
                                    type="number"
                                    value={markup}
                                    onChange={(event) => setMarkup(event.target.value)}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                />
                                <button type="submit" className="btn btn-smth" disabled={isSavingMarkup}>
                                    {isSavingMarkup ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </form>
                </section>

                <nav className="admin-section-tabs" aria-label="Admin dashboard sections">
                    <button type="button" className={activeSection === 'bookings' ? 'active' : ''} onClick={() => setActiveSection('bookings')}>
                        Bookings <span>{dashboardSummary.totalBookings}</span>
                    </button>
                    <button type="button" className={activeSection === 'users' ? 'active' : ''} onClick={() => setActiveSection('users')}>
                        Users <span>{dashboardSummary.totalCustomers}</span>
                    </button>
                    <button type="button" className={activeSection === 'business' ? 'active' : ''} onClick={() => { setActiveSection('business'); fetchBusinessAccounts(1, businessSearch).catch((e) => setError(e.message)); }}>
                        Business accounts <span>{businessData.pagination.total}</span>
                    </button>
                    <button type="button" className={activeSection === 'content' ? 'active' : ''} onClick={() => setActiveSection('content')}>
                        Site content <span>3</span>
                    </button>
                </nav>

                {activeSection === 'bookings' ? (
                <section className="admin-bookings-section">
                    <div className="admin-section-heading">
                        <div>
                            <span className="section-kicker">LIVE OPERATIONS</span>
                            <h2>Booking <em>queue.</em></h2>
                        </div>
                        <p>Search, filter, and inspect every customer shipment from one place.</p>
                    </div>

                    <div className="admin-toolbar">
                        <label className="admin-search">
                            <span className="visually-hidden">Search bookings</span>
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input type="search" placeholder="Search ID, customer, or email…" value={searchQuery} onChange={handleSearch} />
                        </label>
                        <select aria-label="Filter by booking status" value={statusFilter} onChange={(event) => handleFilterChange(setStatusFilter, event.target.value, paymentFilter)}>
                            <option value="">All booking statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Booked">Booked</option>
                            <option value="In Transit">In transit</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                        <select aria-label="Filter by payment status" value={paymentFilter} onChange={(event) => handleFilterChange(setPaymentFilter, event.target.value, statusFilter)}>
                            <option value="">All payment statuses</option>
                            <option value="Pending">Payment pending</option>
                            <option value="Paid">Paid</option>
                            <option value="Failed">Failed</option>
                        </select>
                        {(searchQuery || statusFilter || paymentFilter) && (
                            <button type="button" className="admin-clear-button" onClick={clearFilters}>Clear filters</button>
                        )}
                    </div>

                    <div className="admin-table-meta">
                        <span>Showing <strong>{bookingsData.bookings.length}</strong> of <strong>{bookingsData.pagination.totalBookings}</strong> bookings</span>
                        <span>Page {bookingsData.pagination.currentPage} of {Math.max(bookingsData.pagination.totalPages, 1)}</span>
                    </div>

                    {bookingsData.bookings.length === 0 ? (
                        <div className="admin-empty-state">
                            <span className="section-kicker">NO MATCHES</span>
                            <h3>No bookings found.</h3>
                            <p>Try clearing the filters or searching for a different customer or booking ID.</p>
                            {(searchQuery || statusFilter || paymentFilter) && <button type="button" className="btn btn-smth" onClick={clearFilters}>Clear filters</button>}
                        </div>
                    ) : (
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Booking</th>
                                        <th scope="col">Customer</th>
                                        <th scope="col">Payment</th>
                                        <th scope="col">Status</th>
                                        <th scope="col" className="admin-table-actions-heading">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookingsData.bookings.map((booking) => (
                                        <tr key={booking.Booking_ID}>
                                            <td data-label="Booking">
                                                <strong>#{booking.Booking_ID}</strong>
                                                <span className="admin-table-subtext">Customer #{booking.Cust_ID}</span>
                                            </td>
                                            <td data-label="Customer">
                                                <strong>{booking.Customer_Name}</strong>
                                                <span className="admin-table-subtext">{booking.Customer_Email}</span>
                                            </td>
                                            <td data-label="Payment"><span className={`admin-pill admin-pill-${statusClass(booking.Payment_Status)}`}>{booking.Payment_Status}</span></td>
                                            <td data-label="Status"><span className={`admin-pill admin-pill-${statusClass(booking.Booking_Status)}`}>{booking.Booking_Status}</span></td>
                                            <td data-label="Actions" className="admin-row-actions">
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-secondary"
                                                    onClick={() => fetchBookingDetails(booking.Booking_ID)}
                                                >
                                                    View details
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-primary"
                                                    onClick={() => handleStatusChange(booking.Booking_ID, booking.Booking_Status)}
                                                    disabled={updatingBookingId === booking.Booking_ID}
                                                >
                                                    {updatingBookingId === booking.Booking_ID ? 'Updating…' : isBookedStatus(booking.Booking_Status) ? 'Mark pending' : 'Mark booked'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {bookingsData.pagination.totalPages > 1 && (
                        <div className="admin-pagination">
                            <button type="button" className="admin-page-button" onClick={() => fetchBookingsOnly(bookingsData.pagination.currentPage - 1, searchQuery, statusFilter, paymentFilter)} disabled={bookingsData.pagination.currentPage === 1}>← Previous</button>
                            <span>Page {bookingsData.pagination.currentPage} of {bookingsData.pagination.totalPages}</span>
                            <button type="button" className="admin-page-button" onClick={() => fetchBookingsOnly(bookingsData.pagination.currentPage + 1, searchQuery, statusFilter, paymentFilter)} disabled={bookingsData.pagination.currentPage === bookingsData.pagination.totalPages}>Next →</button>
                        </div>
                    )}
                </section>
                ) : activeSection === 'users' ? (
                <section className="admin-bookings-section">
                    <div className="admin-section-heading">
                        <div>
                            <span className="section-kicker">CUSTOMER DIRECTORY</span>
                            <h2>User <em>accounts.</em></h2>
                        </div>
                        <p>Review customer profiles, inspect booking history, and control account access.</p>
                    </div>

                    <div className="admin-toolbar">
                        <label className="admin-search">
                            <span className="visually-hidden">Search users</span>
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input type="search" placeholder="Search user ID, name, or email…" value={userSearch} onChange={handleUserSearch} />
                        </label>
                        <select aria-label="Filter users by status" value={userStatus} onChange={(event) => handleUserStatusChange(event.target.value)}>
                            <option value="">All account statuses</option>
                            <option value="active">Active users</option>
                            <option value="blocked">Blocked users</option>
                            <option value="deleted">Deleted users</option>
                        </select>
                        {(userSearch || userStatus) && (
                            <button type="button" className="admin-clear-button" onClick={clearUserFilters}>Clear filters</button>
                        )}
                    </div>

                    <div className="admin-table-meta">
                        <span>Showing <strong>{usersData.users.length}</strong> of <strong>{usersData.pagination.totalUsers}</strong> users</span>
                        <span>Page {usersData.pagination.currentPage} of {Math.max(usersData.pagination.totalPages, 1)}</span>
                    </div>

                    {usersData.users.length === 0 ? (
                        <div className="admin-empty-state">
                            <span className="section-kicker">NO MATCHES</span>
                            <h3>No users found.</h3>
                            <p>Try clearing the filters or searching for a different name, email, or customer ID.</p>
                            {(userSearch || userStatus) && <button type="button" className="btn btn-smth" onClick={clearUserFilters}>Clear filters</button>}
                        </div>
                    ) : (
                        <div className="admin-table-wrap">
                            <table className="admin-table admin-users-table">
                                <thead>
                                    <tr>
                                        <th scope="col">User</th>
                                        <th scope="col">Joined</th>
                                        <th scope="col">Bookings</th>
                                        <th scope="col">Status</th>
                                        <th scope="col" className="admin-table-actions-heading">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usersData.users.map((user) => (
                                        <tr key={user.Cust_ID}>
                                            <td data-label="User">
                                                <strong>{user.Name}</strong>
                                                <span className="admin-table-subtext">#{user.Cust_ID} · {user.Email}</span>
                                            </td>
                                            <td data-label="Joined">
                                                <strong>{new Date(user.Created_At).toLocaleDateString('en-AU')}</strong>
                                                <span className="admin-table-subtext">{user.Last_Booking_At ? `Last booking ${new Date(user.Last_Booking_At).toLocaleDateString('en-AU')}` : 'No bookings yet'}</span>
                                            </td>
                                            <td data-label="Bookings"><strong>{user.Booking_Count}</strong></td>
                                            <td data-label="Status">
                                                <span className={`admin-pill ${user.Is_Deleted || user.Is_Blocked ? 'admin-pill-failed' : 'admin-pill-paid'}`}>
                                                    {user.Is_Deleted ? 'Deleted' : user.Is_Blocked ? 'Blocked' : 'Active'}
                                                </span>
                                            </td>
                                            <td data-label="Actions" className="admin-row-actions">
                                                <button type="button" className="admin-action admin-action-secondary" onClick={() => fetchUserDetails(user.Cust_ID)}>View profile</button>
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-primary"
                                                    onClick={() => handleBlockUser(user)}
                                                    disabled={updatingUserId === user.Cust_ID || user.Is_Deleted}
                                                >
                                                    {user.Is_Deleted ? 'Inactive' : updatingUserId === user.Cust_ID ? 'Updating…' : user.Is_Blocked ? 'Unblock' : 'Block'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-danger"
                                                    onClick={() => handleDeleteUser(user)}
                                                    disabled={updatingUserId === user.Cust_ID || user.Is_Deleted}
                                                >
                                                    {user.Is_Deleted ? 'Deleted' : 'Delete'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {usersData.pagination.totalPages > 1 && (
                        <div className="admin-pagination">
                            <button type="button" className="admin-page-button" onClick={() => fetchUsers(usersData.pagination.currentPage - 1)} disabled={usersData.pagination.currentPage === 1}>← Previous</button>
                            <span>Page {usersData.pagination.currentPage} of {usersData.pagination.totalPages}</span>
                            <button type="button" className="admin-page-button" onClick={() => fetchUsers(usersData.pagination.currentPage + 1)} disabled={usersData.pagination.currentPage === usersData.pagination.totalPages}>Next →</button>
                        </div>
                    )}
                </section>
                ) : activeSection === 'business' ? (
                <section className="admin-bookings-section">
                    <div className="admin-section-heading">
                        <div>
                            <span className="section-kicker">BUSINESS ACCOUNTS</span>
                            <h2>Business <em>clients.</em></h2>
                        </div>
                        <p>Create business accounts, set per-client markup rates, and manage access.</p>
                    </div>

                    <div className="admin-toolbar">
                        <label className="admin-search">
                            <span className="visually-hidden">Search business accounts</span>
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input type="search" placeholder="Search name or email…" value={businessSearch} onChange={handleBusinessSearch} />
                        </label>
                        <button
                            type="button"
                            className="btn btn-smth"
                            onClick={() => { setCreateBusinessError(''); setCreateBusinessOpen(true); }}
                            style={{ marginLeft: 'auto' }}
                        >
                            + Create business account
                        </button>
                    </div>

                    <div className="admin-table-meta">
                        <span>Showing <strong>{businessData.accounts.length}</strong> of <strong>{businessData.pagination.total}</strong> business accounts</span>
                    </div>

                    {businessData.accounts.length === 0 ? (
                        <div className="admin-empty-state">
                            <span className="section-kicker">NO ACCOUNTS</span>
                            <h3>No business accounts yet.</h3>
                            <p>Create your first business account to get started.</p>
                            <button type="button" className="btn btn-smth" onClick={() => setCreateBusinessOpen(true)}>Create business account</button>
                        </div>
                    ) : (
                        <div className="admin-table-wrap">
                            <table className="admin-table admin-users-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Account</th>
                                        <th scope="col">Markup</th>
                                        <th scope="col">Bookings</th>
                                        <th scope="col">Status</th>
                                        <th scope="col" className="admin-table-actions-heading">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {businessData.accounts.map((account) => (
                                        <tr key={account.Cust_ID}>
                                            <td data-label="Account">
                                                <strong>{account.Name}</strong>
                                                <span className="admin-table-subtext">
                                                    #{account.Cust_ID} · {account.Email}
                                                    {account.Must_Change_Password && (
                                                        <span style={{ marginLeft: '0.5em', color: '#e67e22', fontWeight: 600 }}>· Awaiting first login</span>
                                                    )}
                                                </span>
                                            </td>
                                            <td data-label="Markup">
                                                <span style={{ fontWeight: 600 }}>{account.Business_Markup ?? 0}%</span>
                                                <span className="admin-table-subtext" style={{ display: 'block' }}>set by account</span>
                                            </td>
                                            <td data-label="Bookings">
                                                {account.Booking_Count ?? 0}
                                            </td>
                                            <td data-label="Status">
                                                <span className={`admin-pill ${account.Is_Deleted || account.Is_Blocked ? 'admin-pill-failed' : 'admin-pill-paid'}`}>
                                                    {account.Is_Deleted ? 'Deleted' : account.Is_Blocked ? 'Blocked' : 'Active'}
                                                </span>
                                            </td>
                                            <td data-label="Actions" className="admin-row-actions">
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-secondary"
                                                    onClick={() => fetchBusinessAccountDetail(account.Cust_ID)}
                                                >
                                                    View details
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-primary"
                                                    onClick={() => handleBlockBusinessAccount(account)}
                                                    disabled={account.Is_Deleted}
                                                >
                                                    {account.Is_Deleted ? 'Inactive' : account.Is_Blocked ? 'Unblock' : 'Block'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admin-action admin-action-danger"
                                                    onClick={() => handleDeleteBusinessAccount(account)}
                                                    disabled={account.Is_Deleted}
                                                >
                                                    {account.Is_Deleted ? 'Deleted' : 'Delete'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {businessData.pagination.totalPages > 1 && (
                        <div className="admin-pagination">
                            <button type="button" className="admin-page-button" onClick={() => fetchBusinessAccounts(businessData.pagination.currentPage - 1, businessSearch)} disabled={businessData.pagination.currentPage === 1}>← Previous</button>
                            <span>Page {businessData.pagination.currentPage} of {businessData.pagination.totalPages}</span>
                            <button type="button" className="admin-page-button" onClick={() => fetchBusinessAccounts(businessData.pagination.currentPage + 1, businessSearch)} disabled={businessData.pagination.currentPage === businessData.pagination.totalPages}>Next →</button>
                        </div>
                    )}
                </section>
                ) : (
                <section className="admin-bookings-section admin-content-section">
                    <div className="admin-section-heading">
                        <div>
                            <span className="section-kicker">CONTENT MANAGEMENT</span>
                            <h2>Public <em>pages.</em></h2>
                        </div>
                        <p>Edit the information customers see on FAQs, Terms and Conditions, and Privacy Policy pages.</p>
                    </div>

                    <div className="admin-content-workspace">
                        <nav className="admin-content-nav" aria-label="Editable site pages">
                            {contentPages.map((page) => (
                                <button
                                    type="button"
                                    key={page.slug}
                                    className={selectedContentSlug === page.slug ? 'active' : ''}
                                    onClick={() => setSelectedContentSlug(page.slug)}
                                >
                                    <span>{page.slug === 'faq' ? 'FAQs' : page.slug === 'terms' ? 'Terms & conditions' : 'Privacy policy'}</span>
                                    <small>{page.updatedAt ? `Updated ${new Date(page.updatedAt).toLocaleDateString('en-AU')}` : 'Not published'}</small>
                                </button>
                            ))}
                        </nav>

                        {contentPages.length > 0 && (() => {
                            const page = contentPages.find((item) => item.slug === selectedContentSlug) || contentPages[0];
                            return (
                                <div className="admin-content-editor">
                                    <div className="admin-content-editor-heading">
                                        <div>
                                            <span className="section-kicker">EDITING</span>
                                            <h3>/{page.slug}</h3>
                                        </div>
                                        <a href={`/${page.slug}`} target="_blank" rel="noreferrer">View live page ↗</a>
                                    </div>
                                    <label>
                                        <span>Page title</span>
                                        <input
                                            type="text"
                                            maxLength="255"
                                            value={page.title}
                                            onChange={(event) => updateContentDraft('title', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Page content</span>
                                        <textarea
                                            maxLength="100000"
                                            value={page.body}
                                            onChange={(event) => updateContentDraft('body', event.target.value)}
                                            rows="22"
                                        />
                                    </label>
                                    <div className="admin-content-help">
                                        <span>Formatting</span>
                                        <p>Start a heading or FAQ question with <code>## </code>. Leave a blank line between each heading and paragraph.</p>
                                    </div>
                                    <div className="admin-content-save">
                                        <span>{page.body.length.toLocaleString()} characters</span>
                                        <button
                                            type="button"
                                            className="btn btn-smth"
                                            onClick={handleSaveContent}
                                            disabled={isSavingContent || !page.title.trim() || !page.body.trim()}
                                        >
                                            {isSavingContent ? 'Saving…' : 'Save & publish'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </section>
                )}
            </div>

            {selectedBookingDetails && (
                <div className="admin-detail-overlay" role="presentation" onClick={() => setSelectedBookingDetails(null)}>
                    <div className="admin-detail-drawer" role="dialog" aria-modal="true" aria-label="Booking details" onClick={(event) => event.stopPropagation()}>
                        <div className="admin-detail-drawer-header">
                            <div>
                                <span className="section-kicker">BOOKING INSPECTOR</span>
                                <h2>Shipment <em>details.</em></h2>
                            </div>
                            <button type="button" className="admin-drawer-close" onClick={() => setSelectedBookingDetails(null)} aria-label="Close booking details">×</button>
                        </div>
                        <DashBookingData
                            bookingDetails={selectedBookingDetails}
                            onReconcileInsurance={reconcileInsurance}
                            isReconcilingInsurance={reconcilingInsuranceId === (selectedBookingDetails.Booking_ID || selectedBookingDetails.bookingId)}
                            insuranceReconcileMessage={insuranceReconcileMessage}
                        />
                    </div>
                </div>
            )}
            {selectedUserDetails && (
                <div className="admin-detail-overlay" role="presentation" onClick={() => setSelectedUserDetails(null)}>
                    <div className="admin-detail-drawer" role="dialog" aria-modal="true" aria-label="User profile" onClick={(event) => event.stopPropagation()}>
                        <div className="admin-detail-drawer-header">
                            <div>
                                <span className="section-kicker">USER PROFILE</span>
                                <h2>{selectedUserDetails.user.Name}<br /><em>#{selectedUserDetails.user.Cust_ID}</em></h2>
                            </div>
                            <button type="button" className="admin-drawer-close" onClick={() => setSelectedUserDetails(null)} aria-label="Close user profile">×</button>
                        </div>
                        <div className="admin-user-profile">
                            <div className="admin-user-profile-summary">
                                <div><span>Email</span><strong>{selectedUserDetails.user.Email}</strong></div>
                                <div><span>Joined</span><strong>{new Date(selectedUserDetails.user.Created_At).toLocaleDateString('en-AU')}</strong></div>
                                <div><span>Account status</span><strong>{selectedUserDetails.user.Is_Deleted ? 'Deleted' : selectedUserDetails.user.Is_Blocked ? 'Blocked' : 'Active'}</strong></div>
                                <div><span>Total bookings</span><strong>{selectedUserDetails.bookings.length}</strong></div>
                            </div>
                            <div className="admin-user-profile-actions">
                                <button
                                    type="button"
                                    className="admin-action admin-action-primary"
                                    onClick={() => handleBlockUser(selectedUserDetails.user)}
                                    disabled={updatingUserId === selectedUserDetails.user.Cust_ID || selectedUserDetails.user.Is_Deleted}
                                >
                                    {selectedUserDetails.user.Is_Deleted ? 'Account inactive' : selectedUserDetails.user.Is_Blocked ? 'Unblock account' : 'Block account'}
                                </button>
                                <button
                                    type="button"
                                    className="admin-action admin-action-danger"
                                    onClick={() => handleDeleteUser(selectedUserDetails.user)}
                                    disabled={updatingUserId === selectedUserDetails.user.Cust_ID || selectedUserDetails.user.Is_Deleted}
                                >
                                    {selectedUserDetails.user.Is_Deleted ? 'Account deleted' : 'Delete account'}
                                </button>
                            </div>
                            <div className="admin-user-bookings">
                                <div className="admin-user-bookings-heading">
                                    <span className="section-kicker">BOOKING HISTORY</span>
                                    <h3>Shipments made by this user</h3>
                                </div>
                                {selectedUserDetails.bookings.length === 0 ? (
                                    <p className="admin-user-no-bookings">This user has not made any bookings.</p>
                                ) : selectedUserDetails.bookings.map((booking) => (
                                    <button
                                        type="button"
                                        className="admin-user-booking"
                                        key={booking.Booking_ID}
                                        onClick={() => { setSelectedUserDetails(null); fetchBookingDetails(booking.Booking_ID); }}
                                    >
                                        <span>
                                            <strong>Booking #{booking.Booking_ID}</strong>
                                            <small>{booking.From_City || 'Origin'} → {booking.To_City || 'Destination'} · {booking.Selected_Courier || 'Courier pending'}</small>
                                        </span>
                                        <span>
                                            <strong>{formatMoney(booking.Courier_Price)}</strong>
                                            <small>{booking.Booking_Status || 'Pending'}</small>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {passwordModalOpen && (
                <div className="cw-modal-backdrop" role="presentation" onClick={closePasswordModal}>
                    <section className="cw-modal cw-account-modal admin-password-modal" role="dialog" aria-modal="true" aria-labelledby="admin-password-title" onClick={(event) => event.stopPropagation()}>
                        <div className="cw-modal-header">
                            <div>
                                <span className="section-kicker">ADMIN SECURITY</span>
                                <h2 id="admin-password-title">Change <em>password.</em></h2>
                            </div>
                            <button type="button" className="cw-modal-close" onClick={closePasswordModal} disabled={isPasswordSubmitting} aria-label="Close">×</button>
                        </div>

                        {passwordStep === 'request' ? (
                            <form onSubmit={handleRequestPasswordOtp}>
                                <div className="cw-modal-body">
                                    <p className="cw-modal-copy">Confirm your current password. We’ll send a six-digit verification code to the email address registered to this admin account.</p>
                                    {passwordError && <div className="admin-password-alert admin-password-alert-error" role="alert">{passwordError}</div>}
                                    <div className="cw-form-grid">
                                        <label className="cw-form-span-2">
                                            Current password
                                            <input
                                                type="password"
                                                autoComplete="current-password"
                                                value={currentPassword}
                                                onChange={(event) => setCurrentPassword(event.target.value)}
                                                required
                                                autoFocus
                                            />
                                        </label>
                                    </div>
                                </div>
                                <div className="cw-modal-footer">
                                    <button type="button" className="btn btn-stroked" onClick={closePasswordModal} disabled={isPasswordSubmitting}>Cancel</button>
                                    <button type="submit" className="btn btn-smth" disabled={isPasswordSubmitting || !currentPassword}>
                                        {isPasswordSubmitting ? 'Sending…' : 'Send verification code'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleConfirmPasswordChange}>
                                <div className="cw-modal-body">
                                    <p className="cw-modal-copy">Enter the code from your email and choose a new password. The code expires in 10 minutes and can only be tried five times.</p>
                                    {passwordMessage && <div className="admin-password-alert admin-password-alert-success" role="status">{passwordMessage}</div>}
                                    {passwordError && <div className="admin-password-alert admin-password-alert-error" role="alert">{passwordError}</div>}
                                    <div className="cw-form-grid">
                                        <label className="cw-form-span-2">
                                            Six-digit verification code
                                            <input
                                                className="admin-otp-input"
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                maxLength="6"
                                                pattern="[0-9]{6}"
                                                value={otp}
                                                onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                                                required
                                                autoFocus
                                            />
                                        </label>
                                        <label>
                                            New password
                                            <input type="password" autoComplete="new-password" minLength="12" maxLength="128" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required />
                                        </label>
                                        <label>
                                            Confirm password
                                            <input type="password" autoComplete="new-password" minLength="12" maxLength="128" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required />
                                        </label>
                                    </div>
                                    <p className="admin-password-hint">Use at least 12 characters. All existing admin sessions will be signed out after the password changes.</p>
                                </div>
                                <div className="cw-modal-footer">
                                    <button type="button" className="btn btn-stroked" onClick={closePasswordModal} disabled={isPasswordSubmitting}>Cancel</button>
                                    <button type="submit" className="btn btn-smth" disabled={isPasswordSubmitting || otp.length !== 6 || !newPassword || !confirmPassword}>
                                        {isPasswordSubmitting ? 'Changing…' : 'Verify & change password'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                </div>
            )}
            {(selectedBusinessAccount || isLoadingBusinessDetail) && (
                <div className="cw-modal-backdrop" role="presentation" onClick={() => setSelectedBusinessAccount(null)}>
                    <section className="cw-modal cw-account-modal admin-password-modal business-account-ledger-modal" role="dialog" aria-modal="true" aria-labelledby="biz-detail-title" onClick={(e) => e.stopPropagation()}>
                        <div className="cw-modal-header">
                            <div>
                                <span className="section-kicker">BUSINESS ACCOUNT</span>
                                <h2 id="biz-detail-title">
                                    {isLoadingBusinessDetail ? 'Loading…' : <>{selectedBusinessAccount.account.Name}<em>.</em></>}
                                </h2>
                            </div>
                            <button type="button" className="cw-modal-close" onClick={() => setSelectedBusinessAccount(null)} aria-label="Close">×</button>
                        </div>

                        {isLoadingBusinessDetail ? (
                            <div className="cw-modal-body"><p style={{ color: 'var(--text-muted,#888)' }}>Loading account details…</p></div>
                        ) : (
                            <div className="cw-modal-body">
                                {/* ── Identity ── */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem', marginBottom: '1.5rem' }}>
                                    <div>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted,#888)' }}>EMAIL</span>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.95rem' }}>{selectedBusinessAccount.account.Email}</p>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted,#888)' }}>ACCOUNT ID</span>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.95rem' }}>#{selectedBusinessAccount.account.Cust_ID}</p>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted,#888)' }}>STATUS</span>
                                        <p style={{ margin: '4px 0 0' }}>
                                            <span className={`admin-pill ${selectedBusinessAccount.account.Is_Deleted || selectedBusinessAccount.account.Is_Blocked ? 'admin-pill-failed' : 'admin-pill-paid'}`}>
                                                {selectedBusinessAccount.account.Is_Deleted ? 'Deleted' : selectedBusinessAccount.account.Is_Blocked ? 'Blocked' : 'Active'}
                                            </span>
                                            {selectedBusinessAccount.account.Must_Change_Password && (
                                                <span style={{ marginLeft: '0.5em', fontSize: '0.78rem', color: '#e67e22', fontWeight: 600 }}>· Awaiting first login</span>
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted,#888)' }}>JOINED</span>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.95rem' }}>
                                            {new Date(selectedBusinessAccount.account.Created_At).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </p>
                                    </div>
                                </div>

                                {/* ── Stats ── */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                    {[
                                        { label: 'THEIR MARKUP', value: `${selectedBusinessAccount.account.Business_Markup ?? 0}%` },
                                        { label: 'TOTAL BOOKINGS', value: selectedBusinessAccount.account.Booking_Count ?? 0 },
                                        { label: 'TOTAL SPENT', value: formatMoney(selectedBusinessAccount.account.Total_Revenue ?? 0) },
                                    ].map(({ label, value }) => (
                                        <div key={label} style={{ padding: '1rem', background: 'var(--surface-alt,#f5f5f3)', borderRadius: '6px', textAlign: 'center' }}>
                                            <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--text-muted,#888)', display: 'block' }}>{label}</span>
                                            <strong style={{ fontSize: '1.35rem', display: 'block', marginTop: '4px' }}>{value}</strong>
                                        </div>
                                    ))}
                                </div>


                                <BusinessAccountLedger accountId={selectedBusinessAccount.account.Cust_ID} />
                            </div>
                        )}

                        <div className="cw-modal-footer">
                            <button type="button" className="btn btn-stroked" onClick={() => setSelectedBusinessAccount(null)}>Close</button>
                        </div>
                    </section>
                </div>
            )}

            {createBusinessOpen && (
                <div className="cw-modal-backdrop" role="presentation" onClick={() => setCreateBusinessOpen(false)}>
                    <section className="cw-modal cw-account-modal admin-password-modal" role="dialog" aria-modal="true" aria-labelledby="create-business-title" onClick={(e) => e.stopPropagation()}>
                        <div className="cw-modal-header">
                            <div>
                                <span className="section-kicker">NEW ACCOUNT</span>
                                <h2 id="create-business-title">Create business <em>account.</em></h2>
                            </div>
                            <button type="button" className="cw-modal-close" onClick={() => setCreateBusinessOpen(false)} disabled={isCreatingBusiness} aria-label="Close">×</button>
                        </div>
                        <form onSubmit={handleCreateBusiness}>
                            <div className="cw-modal-body">
                                <p className="cw-modal-copy">
                                    The business user will receive a welcome email with their login credentials and must set a new password on their first sign-in. Their markup starts at 0% — they can set it themselves from their business profile, or you can update it any time from the accounts list.
                                </p>
                                {createBusinessError && <div className="admin-password-alert admin-password-alert-error" role="alert">{createBusinessError}</div>}
                                <div className="cw-form-grid">
                                    <label className="cw-form-span-2">
                                        Business name
                                        <input
                                            type="text"
                                            value={newBusiness.name}
                                            onChange={(e) => setNewBusiness((p) => ({ ...p, name: e.target.value }))}
                                            required
                                            autoFocus
                                            maxLength="255"
                                        />
                                    </label>
                                    <label className="cw-form-span-2">
                                        Email address
                                        <input
                                            type="email"
                                            value={newBusiness.email}
                                            onChange={(e) => setNewBusiness((p) => ({ ...p, email: e.target.value }))}
                                            required
                                            maxLength="255"
                                        />
                                    </label>
                                    <label className="cw-form-span-2">
                                        Temporary password
                                        <input
                                            type="password"
                                            value={newBusiness.password}
                                            onChange={(e) => setNewBusiness((p) => ({ ...p, password: e.target.value }))}
                                            required
                                            minLength="8"
                                            maxLength="128"
                                        />
                                    </label>
                                </div>
                                <p className="admin-password-hint">The user will be prompted to change their password when they first sign in.</p>
                            </div>
                            <div className="cw-modal-footer">
                                <button type="button" className="btn btn-stroked" onClick={() => setCreateBusinessOpen(false)} disabled={isCreatingBusiness}>Cancel</button>
                                <button type="submit" className="btn btn-smth" disabled={isCreatingBusiness || !newBusiness.name || !newBusiness.email || !newBusiness.password}>
                                    {isCreatingBusiness ? 'Creating…' : 'Create account'}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            )}
        </main>
    );
}

export default Dashboard;